import { useState, useEffect, useContext, useCallback } from "react";
import { Settings, Check, X, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, AuthContext } from "../App";

const EMPTY_20 = () => Array.from({ length: 20 }, () => ({ label: "", text: "" }));

// A row of user-customizable "shoutbox" quick-message buttons above the chat input.
// 20 slots (2 rows of 10). Buttons with an empty label are hidden. No defaults.
const ShoutboxBar = ({ onSend }) => {
  const { token } = useContext(AuthContext);
  const [buttons, setButtons] = useState(EMPTY_20());
  const [showSecondRow, setShowSecondRow] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(EMPTY_20());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/shoutbox`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = res.data.shoutbox_buttons || [];
      const padded = EMPTY_20().map((e, i) => ({
        label: list[i]?.label || "",
        text: list[i]?.text || "",
      }));
      setButtons(padded);
    } catch (e) {
      /* silent */
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const openEditor = () => {
    setDraft(buttons.map((b) => ({ ...b })));
    setEditing(true);
  };

  const saveEditor = async () => {
    setSaving(true);
    try {
      const payload = draft.map((b) => ({ label: (b.label || "").trim(), text: (b.text || "").trim() }));
      const res = await axios.put(
        `${API}/user/shoutbox`,
        { shoutbox_buttons: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const list = res.data.shoutbox_buttons || payload;
      setButtons(EMPTY_20().map((e, i) => ({ label: list[i]?.label || "", text: list[i]?.text || "" })));
      setEditing(false);
      toast.success("Shoutbox buttons saved!");
    } catch (e) {
      toast.error("Couldn't save your buttons");
    } finally {
      setSaving(false);
    }
  };

  // Only buttons with a non-empty label are shown.
  const renderButtons = (start, end) =>
    buttons.slice(start, end).map((b, i) => {
      const idx = start + i;
      if (!b.label) return null;
      return (
        <button
          key={idx}
          className="retro-button text-[11px] px-2 py-1 whitespace-nowrap hover:bg-[#000080] hover:text-white transition-colors"
          onClick={() => b.text && onSend?.(b.text)}
          title={b.text}
          data-testid={`shoutbox-btn-${idx}`}
        >
          {b.label}
        </button>
      );
    });

  const secondRowHasButtons = buttons.slice(10, 20).some((b) => b.label);

  if (editing) {
    return (
      <div className="bg-[#C0C0C0] border-t border-[#808080] p-2" data-testid="shoutbox-editor">
        <div className="flex items-center justify-between mb-2">
          <span className="font-arial text-xs font-bold">Customize your Shoutbox (Label + Message · empty labels are hidden)</span>
          <div className="flex gap-1">
            <button className="retro-button px-2 py-0.5 text-[11px] flex items-center gap-1" onClick={saveEditor} disabled={saving} data-testid="shoutbox-save-btn">
              <Check className="w-3 h-3" /> {saving ? "Saving..." : "Save"}
            </button>
            <button className="retro-button px-2 py-0.5 text-[11px] flex items-center gap-1" onClick={() => setEditing(false)} data-testid="shoutbox-cancel-btn">
              <X className="w-3 h-3" /> Cancel
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-48 overflow-y-auto retro-scrollbar pr-1">
          {draft.map((b, i) => (
            <div key={i} className="flex gap-1 items-center">
              <span className="text-[10px] w-5 text-right text-[#555]">{i + 1}</span>
              <input
                className="retro-input text-[11px] px-1 py-0.5 w-20 flex-shrink-0"
                placeholder="Label"
                value={b.label}
                maxLength={24}
                onChange={(e) => setDraft((d) => d.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                data-testid={`shoutbox-label-${i}`}
              />
              <input
                className="retro-input text-[11px] px-1 py-0.5 flex-1 min-w-0"
                placeholder="Message to send"
                value={b.text}
                maxLength={300}
                onChange={(e) => setDraft((d) => d.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                data-testid={`shoutbox-message-${i}`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#C0C0C0] border-t border-[#808080] px-2 pt-1.5" data-testid="shoutbox-bar">
      <div className="flex flex-wrap gap-1 items-center">
        {renderButtons(0, 10)}
        {secondRowHasButtons && (
          <button
            className="retro-button text-[11px] px-1.5 py-1 flex items-center gap-0.5"
            onClick={() => setShowSecondRow((s) => !s)}
            title="More buttons"
            data-testid="shoutbox-more-btn"
          >
            {showSecondRow ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            More
          </button>
        )}
        <button
          className="retro-button text-[11px] px-2 py-1 flex items-center gap-1"
          onClick={openEditor}
          title="Customize your shoutbox buttons"
          data-testid="shoutbox-customize-btn"
        >
          <Settings className="w-3 h-3" /> Customize
        </button>
      </div>
      {showSecondRow && secondRowHasButtons && (
        <div className="flex flex-wrap gap-1 items-center mt-1" data-testid="shoutbox-row-2">
          {renderButtons(10, 20)}
        </div>
      )}
    </div>
  );
};

export default ShoutboxBar;
